import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileStack, Users, DollarSign, Clock, ArrowRight, Loader2, Filter, AlertTriangle, LayoutDashboard, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { SERVICE_MAP, STATUS_META } from "@/data/services";
import { DeadlinePill } from "@/components/DeadlineWatch";

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.submitted;
  return <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${m.cls}`}>{m.label}</span>;
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${accent}`}><Icon className="w-5 h-5" /></div>
    <p className="font-sans font-bold text-3xl text-brand-navy tabular-nums" style={{ fontVariantNumeric: "lining-nums tabular-nums" }}>{value}</p>
    <p className="text-sm text-slate-500 mt-0.5">{label}</p>
  </div>
);

const FILTERS = ["all", "submitted", "quoted", "paid", "processing", "completed"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([api.get("/admin/stats"), api.get("/applications")]);
        setStats(s.data); setApps(a.data);
      } catch (e) { toast.error(apiErr(e)); setApps([]); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!apps) return [];
    const q = query.trim().toLowerCase();
    return apps.filter((a) => {
      if (filter !== "all" && a.status !== filter) return false;
      if (!q) return true;
      const hay = `${a.reference_no || ""} ${a.applicant_name || ""} ${a.applicant_email || ""} ${SERVICE_MAP[a.service_type]?.title || a.service_type || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [apps, filter, query]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-red transition-colors mb-4" data-testid="back-to-portal-link">
        <LayoutDashboard className="w-4 h-4" /> Portal home
      </Link>
      <p className="text-xs uppercase tracking-[0.2em] text-brand-red font-bold">Admin</p>
      <h1 className="font-serif text-4xl text-brand-navy tracking-tight mt-1">Applications overview</h1>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FileStack} label="Total applications" value={stats?.total_applications ?? "—"} accent="bg-brand-red/10 text-brand-red" />
        <StatCard icon={Clock} label="Awaiting quotation" value={stats?.by_status?.submitted ?? 0} accent="bg-amber-100 text-brand-amber" />
        <StatCard icon={DollarSign} label="Revenue (USD)" value={stats ? Number(stats.revenue).toLocaleString() : "—"} accent="bg-emerald-100 text-emerald-600" />
        <StatCard icon={Users} label="Registered users" value={stats?.total_users ?? "—"} accent="bg-slate-100 text-brand-navy" />
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="admin-search-input"
            placeholder="Search reference, applicant or service…"
            className="w-full rounded-full border border-slate-200 bg-white pl-10 pr-4 py-2 text-sm outline-none focus:border-brand-red transition-colors" />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400" />
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors capitalize ${filter === f ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-slate-200 hover:border-brand-navy"}`}>
            {f === "all" ? "All" : STATUS_META[f].label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {apps === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-brand-red animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-3xl border border-dashed border-slate-300 bg-slate-50 text-slate-500">No applications in this view.</div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
              <span className="col-span-3">Reference</span><span className="col-span-3">Service</span>
              <span className="col-span-3">Applicant</span><span className="col-span-2">Status</span><span className="col-span-1"></span>
            </div>
            {filtered.map((a, i) => (
              <motion.div key={a.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}>
                <Link to={`/admin/applications/${a.id}`} data-testid={`admin-app-row-${a.id}`}
                  className="group grid grid-cols-2 sm:grid-cols-12 gap-4 items-center px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <span className="col-span-2 sm:col-span-3 font-mono text-xs text-brand-navy">{a.reference_no}</span>
                  <span className="col-span-2 sm:col-span-3 text-sm text-slate-700">{SERVICE_MAP[a.service_type]?.title || a.service_type}</span>
                  <span className="col-span-1 sm:col-span-3 text-sm text-slate-600 truncate">{a.applicant_name || a.applicant_email}</span>
                  <span className="col-span-1 sm:col-span-2 flex items-center gap-1.5 flex-wrap">
                    <StatusPill status={a.status} />
                    <DeadlinePill app={a} />
                    {a.needs_action && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-brand-red border border-red-200" data-testid={`action-badge-${a.id}`}>
                        <AlertTriangle className="w-3 h-3" /> Action
                      </span>
                    )}
                  </span>
                  <span className="hidden sm:flex sm:col-span-1 justify-end"><ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-brand-red group-hover:translate-x-1 transition-all" /></span>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
